import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  PlusCircle,
  Wrench,
  Edit,
  Trash2,
  MoreVertical,
  Plus,
  X,
  Loader2,
} from "lucide-react";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { DataTable } from "@/components/ui/data-table";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { ReactSelect } from "@/components/ui/react-select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Spinner } from "@/components/ui/spinner";
import { all_routes } from "../../Router/all_routes";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/layout/PageContainer";

const PredefinedRepairs = () => {
  const permissions = useSelector((state) => state.userpermission_data);
  const hasPermission = React.useCallback(
    (permission) => {
      return permissions && permissions.includes(permission);
    },
    [permissions],
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [deleteConfirmationId, setDeleteConfirmationId] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });

  // Reset pagination when filters change
  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [
    searchText,
    priorityFilter,
    isActiveFilter,
    categoryFilter,
    sortBy,
    sortDirection,
  ]);

  React.useEffect(() => {
    if (!hasPermission("predefined repair job read")) {
      toast.error("Unauthorized", {
        description:
          "You do not have permission to view this page. Redirecting to dashboard...",
      });
      setTimeout(() => {
        navigate(all_routes.dashboard);
      }, 3000);
    }
  }, [hasPermission, navigate]);

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  // ==================== QUERIES ====================
  const { data: repairData, isLoading: isRepairsLoading } = useQuery({
    queryKey: [
      "predefined-repairs",
      pagination.pageIndex,
      pagination.pageSize,
      searchText,
      priorityFilter,
      isActiveFilter,
      categoryFilter,
      sortBy,
      sortDirection,
    ],
    queryFn: async () => {
      const params = {
        page: pagination.pageIndex + 1,
        per_page: pagination.pageSize,
        with: ["repairCategories"],
      };
      if (searchText) params.search = searchText;
      if (priorityFilter) params.priority = priorityFilter;
      if (isActiveFilter !== "") params.is_active = isActiveFilter === "true";
      if (categoryFilter.length > 0)
        params.repair_category_ids = categoryFilter.map((c) => c.value);
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_direction = sortDirection;
      }
      const res = await apiClient.get(API_ENDPOINTS.REPAIRS.PREDEFINED_JOBS, {
        params,
      });
      return res.data;
    },
  });

  const repairs = repairData?.data || [];
  const pageCount = repairData?.meta?.last_page || 1;
  const totalCount = repairData?.meta?.total || 0;

  const { data: repairCategories = [] } = useQuery({
    queryKey: ["repair-categories"],
    queryFn: async () => {
      const res = await apiClient.get("/category_repair");
      return res.data.data || [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(API_ENDPOINTS.REPAIRS.PREDEFINED_JOB_DETAILS(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["predefined-repairs"] });
      toast.success("Predefined repair job deleted successfully");
      setDeleteConfirmationId(null);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          "Failed to delete predefined repair job",
      );
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }) => {
      await apiClient.patch(API_ENDPOINTS.REPAIRS.TOGGLE_ACTIVE(id), {
        is_active,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["predefined-repairs"] });
      toast.success("Status updated successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update status");
    },
  });

  // ==================== HELPERS ====================
  const handleToggleActive = (id, currentStatus) => {
    toggleActiveMutation.mutate({ id, is_active: !currentStatus });
  };

  const handleAdd = () => {
    navigate(all_routes.addPredefinedRepair);
  };

  const handleEdit = (repair) => {
    navigate(`${all_routes.editPredefinedRepair.replace(":id", repair.id)}`);
  };

  const handleCreateRepairJob = (predefinedRepairId) => {
    navigate(all_routes.repairPos + `?predefined=${predefinedRepairId}`);
  };

  const getPriorityBadge = (priority) => {
    const p = (priority || "").toLowerCase();
    const priorityConfig = {
      critical: { variant: "danger" },
      urgent: { variant: "danger" },
      high: { variant: "warning" },
      normal: { variant: "info" },
      low: { variant: "success" },
    };

    const config = priorityConfig[p] || {
      variant: "secondary",
    };

    return (
      <Badge variant={config.variant}>
        {priority?.charAt(0).toUpperCase() + priority?.slice(1)}
      </Badge>
    );
  };

  // ==================== TABLE COLUMNS ====================
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorFn: (row) => {
          if (!row.repair_categories || row.repair_categories.length === 0)
            return "-";
          return row.repair_categories.map((cat) => cat.name).join(", ");
        },
        header: "Category",
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <div className="max-w-[200px] truncate" title={value}>
              {value}
            </div>
          );
        },
      },
      {
        accessorKey: "item_description",
        header: "Item",
        cell: ({ getValue }) => {
          const value = getValue();
          const text = value?.replace(/<[^>]*>/g, "") || "-";
          return (
            <div className="max-w-[150px] truncate" title={text}>
              {text}
            </div>
          );
        },
      },
      {
        accessorKey: "issue_description",
        header: "Issue",
        cell: ({ getValue }) => {
          const value = getValue();
          const text = value?.replace(/<[^>]*>/g, "") || "-";
          return (
            <div className="max-w-[150px] truncate" title={text}>
              {text}
            </div>
          );
        },
      },
      {
        accessorKey: "estimated_cost",
        header: "Est. Cost",
        cell: ({ getValue }) =>
          `${currencySymbol}${parseFloat(getValue() || 0).toFixed(2)}`,
      },
      {
        accessorKey: "priority",
        header: "Priority",
        cell: ({ getValue }) => getPriorityBadge(getValue()),
      },
      {
        accessorKey: "is_active",
        header: "Status",
        cell: ({ row }) => {
          const repair = row.original;
          const isActive = repair.is_active;
          return (
            <div className="flex items-center gap-2">
              <Switch
                checked={isActive}
                onCheckedChange={() => handleToggleActive(repair.id, isActive)}
                disabled={
                  !hasPermission("predefined repair job toggle_active") ||
                  toggleActiveMutation.isPending
                }
              />
              <Badge variant={isActive ? "success" : "outline"}>
                {isActive ? "Active" : "Inactive"}
              </Badge>
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const repair = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                {hasPermission("predefined repair job create_repair_job") && (
                  <DropdownMenuItem
                    onClick={() => handleCreateRepairJob(repair.id)}
                  >
                    <Plus className="mr-2 h-4 w-4" /> Create Repair Job
                  </DropdownMenuItem>
                )}
                {hasPermission("predefined repair job update") && (
                  <DropdownMenuItem onClick={() => handleEdit(repair)}>
                    <Edit className="mr-2 h-4 w-4" /> Edit
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {hasPermission("predefined repair job delete") && (
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={() => setDeleteConfirmationId(repair.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currencySymbol],
  );

  if (isRepairsLoading) {
    return (
      <div className="flex h-[100px] w-full items-center justify-center">
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Predefined Repair Jobs"
          subtitle="Manage predefined repair job templates"
        />
        <PageHeaderActions>
          {hasPermission("predefined repair job create") && (
            <Button onClick={handleAdd}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add New Repair Job
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search repairs..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Select
          value={priorityFilter || "all"}
          onValueChange={(v) => setPriorityFilter(v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={isActiveFilter || "all"}
          onValueChange={(v) => setIsActiveFilter(v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <div className="w-56">
          <ReactSelect
            isMulti
            placeholder="All Categories"
            options={repairCategories.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            value={categoryFilter}
            onChange={(val) => setCategoryFilter(val || [])}
          />
        </div>
        <Select
          value={sortBy ? `${sortBy}:${sortDirection}` : "default"}
          onValueChange={(val) => {
            if (val === "default") {
              setSortBy("");
              return;
            }
            const [field, dir] = val.split(":");
            setSortBy(field);
            setSortDirection(dir);
          }}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Default Order</SelectItem>
            <SelectItem value="item_description:asc">Item A→Z</SelectItem>
            <SelectItem value="item_description:desc">Item Z→A</SelectItem>
            <SelectItem value="priority:asc">Priority ↑</SelectItem>
            <SelectItem value="priority:desc">Priority ↓</SelectItem>
            <SelectItem value="estimated_cost:asc">Cost ↑</SelectItem>
            <SelectItem value="estimated_cost:desc">Cost ↓</SelectItem>
            <SelectItem value="created_at:desc">Newest First</SelectItem>
            <SelectItem value="created_at:asc">Oldest First</SelectItem>
          </SelectContent>
        </Select>
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={repairs}
          getRowId={(row) => row.id}
          enableRowSelection={true}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          pageCount={pageCount}
          pagination={pagination}
          onPaginationChange={setPagination}
          isLoading={isRepairsLoading}
          footerInfo={
            <div className="flex items-center gap-4">
              <span className="font-medium text-foreground">
                Total repairs: {totalCount}
              </span>
            </div>
          }
        />
      </TableCard>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteConfirmationId}
        onOpenChange={() => setDeleteConfirmationId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              predefined repair job.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate(deleteConfirmationId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default PredefinedRepairs;



